import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useVersionUpdate } from "@/hooks/useVersionUpdate";
import { useAuth } from "@/auth/AuthContext";

// =============================================================================
// VersionUpdatePrompt — mid-session "a new version shipped" prompt.
//
// Two stages, by design:
//   1. A centered, backdrop-blocking MODAL — unmissable. It can't be closed by
//      the backdrop or Escape, only by its two buttons. But it IS escapable
//      via "Later", so a user in the middle of a booking can finish first —
//      we never force a reload that would drop a half-filled form.
//   2. "Later" collapses it to a persistent bottom-right CORNER banner that
//      stays until they refresh (no further dismiss).
//
// Deploys are infrequent (≈1–2/week) and this is production, so an upfront
// interrupt to get everyone onto the latest build is worth it. Both stages
// share ONE useVersionUpdate() instance (one poll loop, one baseline).
// =============================================================================

export function VersionUpdatePrompt() {
  const { state } = useAuth();
  const { updateAvailable, refresh } = useVersionUpdate();
  const [deferred, setDeferred] = useState(false);

  // Never block the login / invitation / reset screens with a modal — only
  // prompt signed-in users mid-session.
  if (state.status !== "authenticated" || !updateAvailable) return null;

  // Stage 2 — collapsed corner nudge, persists until refresh.
  if (deferred) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-5 right-5 z-[55] w-[min(26rem,calc(100vw-2rem))] animate-slide-up rounded-2xl border border-primary/55 bg-card/95 p-4 shadow-2xl backdrop-blur"
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <strong className="text-sm font-bold text-blue-700 dark:text-primary">
              Update available
            </strong>
            <span className="text-sm text-muted-foreground">
              A newer version is ready — refresh when you&rsquo;re set.
            </span>
          </div>
          <Button size="sm" onClick={refresh} className="flex-shrink-0">
            Refresh now
          </Button>
        </div>
      </div>
    );
  }

  // Stage 1 — centered, blocking-but-escapable modal.
  return (
    <Dialog open>
      <DialogContent
        hideCloseButton
        structured
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="max-w-[480px]"
      >
        <DialogHeader icon={Sparkles} className="pb-6">
          <DialogTitle>A new version is ready</DialogTitle>
          <DialogDescription>
            We&rsquo;ve shipped an update to the portal. Refresh to get the
            latest — it only takes a moment. In the middle of a booking? Tap
            &ldquo;Later,&rdquo; finish up, then refresh.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setDeferred(true)}>
            Later
          </Button>
          <Button onClick={refresh}>Refresh now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
